import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import { Star } from 'lucide-react';

const ClienteRichiesteArchiviate = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [archivedRequests, setArchivedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingReviewTokens, setPendingReviewTokens] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login-form?returnTo=/cliente/richieste/archiviate');
      return;
    }

    const fetchArchivedRequests = async () => {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('client_id', user?.id)
        ?.in('status', ['completed', 'archived', 'cancelled'])
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching archived requests:', error);
      } else {
        setArchivedRequests(data || []);

        // Fetch open review_invitations for these requests
        const requestIds = (data || [])?.map(r => r?.id);
        if (requestIds?.length > 0) {
          const { data: invs } = await supabase?.from('review_invitations')?.select('token, service_request_id')?.in('service_request_id', requestIds)?.is('used_at', null);

          const tokenMap = {};
          (invs || [])?.forEach(inv => {
            tokenMap[inv.service_request_id] = inv?.token;
          });
          setPendingReviewTokens(tokenMap);
        }
      }
      setLoading(false);
    };

    fetchArchivedRequests();
  }, [user, authLoading, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Attiva',
      pending: 'In attesa',
      accepted: 'Accettata',
      in_progress: 'In corso',
      completed: 'Completata',
      cancelled: 'Annullata',
      archived: 'Archiviata',
    };
    return labels?.[status] || status;
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'completed':
        return { backgroundColor: '#dcfce7', color: '#16a34a' };
      case 'cancelled':
        return { backgroundColor: '#fee2e2', color: '#dc2626' };
      case 'archived':
      default:
        return { backgroundColor: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      urgent: 'Urgente',
      high: 'Alta',
      medium: 'Media',
      low: 'Bassa',
    };
    return labels?.[urgency] || urgency;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col pt-16"
      style={{
        background: "linear-gradient(180deg, #ffffff 0%, #f5f9fd 50%, #ffffff 100%)",
        minHeight: '100vh'
      }}
    >
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8">
        {/* Back link - top left */}
        <a
          href="/cliente/dashboard"
          onClick={(e) => { e?.preventDefault(); navigate('/cliente/dashboard'); }}
          style={{ color: '#6b7280', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '14px', marginBottom: '20px' }}
        >
          ← Indietro
        </a>

        {/* Page title */}
        <h1 className="text-xl font-semibold text-gray-700 mb-6 flex items-center gap-2">
          <span>📁</span>
          Richieste Archiviate
        </h1>

        {/* Archived requests list */}
        {archivedRequests?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {archivedRequests?.map((request) => (
              <div
                key={request?.id}
                className="hover:shadow-md transition-shadow"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '24px',
                  borderLeft: '4px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                {/* Clickable area */}
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/cliente/richieste/${request?.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      navigate(`/cliente/richieste/${request?.id}`);
                    }
                  }}
                >
                  <div className="mb-3">
                    <h4 className="text-base font-semibold text-gray-700 mb-1">{request?.title}</h4>
                    {request?.description && (
                      <p className="text-sm text-gray-400 line-clamp-2">{request?.description}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          ...getStatusBadgeStyle(request?.status),
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        {getStatusLabel(request?.status)}
                      </span>
                    </div>

                    {request?.urgency && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">Urgenza:</span>
                        <span className="text-xs text-gray-500 font-medium">{getUrgencyLabel(request?.urgency)}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1 ml-auto">
                      <span className="text-xs text-gray-400">{formatDate(request?.created_at)}</span>
                    </div>
                  </div>

                  {request?.location_name && (
                    <div className="flex items-center gap-1 mt-2">
                      <Icon name="MapPin" size={13} color="#9ca3af" />
                      <span className="text-xs text-gray-400">{request?.location_name}</span>
                    </div>
                  )}
                </div>

                {/* Lascia recensione button — only if pending token exists */}
                {(request?.status === 'completed' || request?.status === 'archived') && pendingReviewTokens?.[request?.id] && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        navigate(`/review/${pendingReviewTokens?.[request?.id]}`);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1.5px solid #2780b5',
                        backgroundColor: 'transparent',
                        color: '#2780b5',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s, color 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <Star size={16} color="#fbbf24" fill="#fbbf24" />
                      Lascia recensione
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '56px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            <Icon name="Archive" size={44} color="#d1d5db" />
            <p className="text-gray-400 text-sm">Nessuna richiesta archiviata</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteRichiesteArchiviate;
